import i18next from 'i18next';
import { WCEnvironment } from '../../types';
import { FintechTokenProvider, TokenResult } from './types';

const AUTH_ERROR_FALLBACKS = {
  providerNotConfigured: 'FintechTokenProvider não configurado: chame setTokenProvider antes de iniciar o componente.',
  customerTokenUnavailable: 'Não foi possível obter o token de autenticação!',
  adminTokenUnavailable: 'Não foi possível obter o token de administrador!',
} as const;

type AuthErrorKey = keyof typeof AUTH_ERROR_FALLBACKS;

/**
 * Servico responsavel por gerenciar os JWTs interinos consumidos pelos
 * componentes da biblioteca.
 *
 * O token de integrador nao trafega mais pelo browser: quem
 * hospeda os componentes injeta um {@link FintechTokenProvider} via
 * `setTokenProvider` e o servico apenas delega a obtencao dos tokens ao
 * provider, mantendo cache em memoria com invalidacao por expiracao.
 *
 * O cache de tokens customer e diferenciado por escopo: dois JWTs
 * distintos sao emitidos para o mesmo `customerId` quando o consumidor
 * pede token sem escopo (contrato ativo) e depois com escopo `['SCORE']`
 * (trial). Sem essa diferenciacao um token de trial poderia ser servido
 * do cache para um consumer que ja tem contrato — regressao de UX.
 */
class FintechAuthService {
  private static instance: FintechAuthService | null = null;
  private tokenProvider?: FintechTokenProvider;
  private readonly tokenCache: Map<string, { token: string; expiresAt: number | null }> = new Map();
  private scopes: string[] | null = null;
  private adminToken: string | null = null;
  private adminTokenExpiration: number | null = null;
  private environment: WCEnvironment | null = null;
  private i18n?: typeof i18next;

  /**
   * Registra a instancia de i18n do componente hospedeiro para que o servico
   * possa traduzir as mensagens de erro repassadas ao usuario.
   *
   * Sem instancia configurada o servico cai no fallback em pt-BR — util em
   * testes unitarios que nao inicializam a camada de i18n.
   */
  public setI18n(i18n: typeof i18next): void {
    this.i18n = i18n;
  }

  private translateAuthError(key: AuthErrorKey): string {
    const fallback = AUTH_ERROR_FALLBACKS[key];
    const translated = this.i18n?.t?.(`common.authError.${key}`);
    if (typeof translated === 'string' && translated && translated !== `common.authError.${key}`) {
      return translated;
    }
    return fallback;
  }

  static getInstance() {
    if (!FintechAuthService.instance) {
      FintechAuthService.instance = new FintechAuthService();
    }
    return FintechAuthService.instance;
  }

  // Mantem-se o metodo por compatibilidade com o interceptor do FintechAPICaller.
  public shouldFetchToken(url: string) {
    return !!url;
  }

  public async getAccessToken(url?: string): Promise<string> {
    if (url?.includes('/admin/')) {
      return this.resolveAdminToken();
    }

    const cacheKey = this.buildCacheKey(this.scopes ?? undefined);
    const cached = this.tokenCache.get(cacheKey);

    if (cached && !this.isEntryExpired(cached)) {
      return cached.token;
    }

    return this.fetchToken(cacheKey);
  }

  /**
   * Limpa qualquer cache/escopo remanescente de componentes anteriores.
   *
   * @param environment ambiente da API Fintech (`local`, `dev`, `hml`, `prd`).
   */
  public initialize(environment: WCEnvironment) {    
    this.environment = environment;
    this.tokenCache.clear();
    this.scopes = null;
    this.adminToken = null;
    this.adminTokenExpiration = null;
  }

  public getEnvironment(): WCEnvironment | null {
    return this.environment;
  }

  /**
   * Registra o {@link FintechTokenProvider} injetado pelo host e invalida
   * caches customer/admin para forcar a proxima obtencao via provider.
   */
  public setTokenProvider(provider: FintechTokenProvider): void {
    this.tokenProvider = provider;
    this.tokenCache.clear();
    this.adminToken = null;
    this.adminTokenExpiration = null;
  }

  /**
   * Define os escopos a serem repassados ao provider na proxima emissao
   * de JWT customer.
   *
   * `null` ou array vazio limpam o estado interno — o proximo pedido de
   * token customer volta a ser feito sem escopo (comportamento pleno).
   * Chamar com `['SCORE']` faz o provider incluir o escopo `SCORE` no
   * body enviado ao SPBean `AutenticacaoFintechSP`, autorizando a
   * jornada trial de `/api/v1/scores/**` para clientes sem contrato.
   */
  public setScopes(scopes: string[] | null): void {
    if (!scopes || scopes.length === 0) {
      this.scopes = null;
      return;
    }
    this.scopes = [...scopes];
  }

  private buildCacheKey(scopes?: string[]): string {
    if (!scopes || scopes.length === 0) {
      return '__no_scope__';
    }
    return [...scopes].sort((a, b) => a.localeCompare(b)).join(',');
  }

  private async fetchToken(cacheKey: string): Promise<string> {
    if (!this.tokenProvider) {
      throw new Error(this.translateAuthError('providerNotConfigured'));
    }

    const result: TokenResult = await this.tokenProvider.getCustomerToken(this.scopes ?? undefined);
    if (!result?.token) {
      throw new Error(this.translateAuthError('customerTokenUnavailable'));
    }

    const expiresAt = typeof result.expiresAt === 'number' ? result.expiresAt : null;
    this.tokenCache.set(cacheKey, { token: result.token, expiresAt });
    return result.token;
  }

  private isEntryExpired(entry: { token: string; expiresAt: number | null }): boolean {
    if (entry.expiresAt === null) {
      return true;
    }
    return Date.now() >= entry.expiresAt;
  }

  public async resolveAdminToken(): Promise<string> {
    if (this.adminToken && !this.isAdminTokenExpired()) {
      return this.adminToken;
    }

    await this.fetchAdminToken();
    return this.adminToken;
  }

  private async fetchAdminToken(): Promise<void> {
    if (!this.tokenProvider) {
      throw new Error(this.translateAuthError('providerNotConfigured'));
    }

    const result: TokenResult = await this.tokenProvider.getAdminToken();
    if (!result?.token) {
      throw new Error(this.translateAuthError('adminTokenUnavailable'));
    }

    this.setAdminToken(result.token);

    if (typeof result.expiresAt === 'number') {
      this.adminTokenExpiration = result.expiresAt;
    }
  }

  private setAdminToken(token: string) {
    this.adminToken = token;
  }

  private isAdminTokenExpired(): boolean {
    if (!this.adminTokenExpiration) {
      return true;
    }
    return Date.now() >= this.adminTokenExpiration;
  }
}

export default FintechAuthService;
